
import { DfAssignEntityService } from './df-assign-entity.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { UntypedFormGroup, FormControl, FormArray, Validators, UntypedFormBuilder } from '@angular/forms';
import { catchError, forkJoin, of, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, generalConfigFromGlobal, hydratedGlobalParams, normalizeFilterParams, rowPayloadFromActiveScreen } from '../../../shared/workspace-element-context';

import * as _ from 'lodash-es';
@Component({
    selector: 'gtw-df-assign-entity',
    templateUrl: './df-assign-entity.component.html',
    styleUrls: ['./df-assign-entity.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DfAssignEntityComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  formRows: any;
  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private dfAssignEntityService: DfAssignEntityService,
    private gtwSnackbarService: GtwSnackbarService,
    private fb: UntypedFormBuilder
  ) {
  }


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  form: UntypedFormGroup = new UntypedFormGroup({});
  initializeComplete: boolean = true;
  loader: boolean = true;
  rowData: any;
  baseURLs: any = { 'admin': '', 'api': '', "custom": '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };
  entityAssignData: any;
  tax_year: any;
  scenario: any = "";
  private scenarioCode: any = '';
  private isIssueKeyEnabled: string = '';
  private issueKey: number = 0;
  ssoId: string = '';
  filterParams: any = {};
  headerData: any = {};
  dfDetails: any;
  ptrDropdown: any;
  pshipDropdown: any;
  details: any;
  isLoadingMap: { [key: number]: boolean } = {};
  ptrDropdownMap: { [key: number]: any[] } = [];

  baseGlobalParams:
    | {
        jcd_key: string;
        tax_year: string;
        scenario: string;
        client_key: string;
        scenarioCode?: string;
        scenario_code?: string;
        is_issue_key_enabled?: string;
        issue_key?: number;
      }
    | undefined;

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.loadData();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.loadData();
    }));
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.tax_year = this.filterParams?.tax_year || this.baseGlobalParams?.tax_year || this.rowData?.TAX_YEAR || '';
    this.scenario = this.filterParams?.scenario || this.baseGlobalParams?.scenario || this.rowData?.SCENARIO || '';
    this.scenarioCode = this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.baseGlobalParams?.scenarioCode || 'RAF';
    this.isIssueKeyEnabled = this.filterParams?.is_issue_key_enabled || this.baseGlobalParams?.is_issue_key_enabled || 'N';
    this.ssoId = this.gtwApp?.USER_SETTINGS?.user?.sso_id || '';
  }

  loadData() {
    let params = {
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key,
      stage_key: this.rowData?.TRANS_NO,
    }
    this.dfAssignEntityService.getEntities(this.baseURLs.api, params).pipe(
      catchError(error => {
        console.error('An error occurred:', error);
        this.loader = false;
        return of(null);
      }), takeUntilDestroyed(this.destroyRef)
    ).subscribe((data: any) => {
      if (data) {
        this.entityAssignData = data;
        this.dfDetails = data[0].jsonObject;
        if (!this.dfDetails || this.dfDetails.length === 0) {
          this.gtwSnackbarService.openSnackBar("No data found", 'gtw-snackbar--red');
          return;
        }
        this.ptrDropdownMap = {};

        forkJoin(
          this.dfDetails.map((detail: any, index: any) =>
            this.dfAssignEntityService.fetchPtrDropdown(detail.PSHIP_CC_KEY, this.baseURLs.api, params).pipe(
              catchError(error => {
                console.error(`Error fetching dropdown for detail at index ${index}:`, error);
                return of([]);
              })
            )
          )
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((results: any) => {
          (results as any[]).forEach((result: any, index: number) => {
            this.ptrDropdownMap[index] = result.jsonObject as any[];
          });
          this.pshipDropdown = data[1].jsonObject;
          if (!this.pshipDropdown || this.pshipDropdown.length === 0) {
            console.log('pshipDropdown is empty');
          }

          this.formRows = this.fb.array([]);
          this.form = this.fb.group({
            rows: this.formRows
          });
          this.dfDetails.forEach((detail: { PSHIP_NAME: any; PSHIP_EIN: any; PTR_NAME: any; PTR_EIN: any; PSHIP_CC_KEY: any; PTR_CC_KEY: any; }) => {
            this.details = this.findDetails(detail, this.ptrDropdownMap, this.formRows.length);
            const row = this.fb.group({
              TaxYear: [this.tax_year, Validators.required],
              InvestmentName: [detail.PSHIP_NAME, Validators.required],
              InvestmentTaxId: [detail.PSHIP_EIN, Validators.required],
              InvestorName: [detail.PTR_NAME, Validators.required],
              InvestorTaxId: [detail.PTR_EIN, Validators.required],
              Old_Pship_CC_Key: [detail.PSHIP_CC_KEY || ''],
              Old_Ptr_CC_key: [detail.PTR_CC_KEY || ''],
              Pship_CC_Key: [detail.PSHIP_CC_KEY || ''],
              Ptr_CC_key: [detail.PTR_CC_KEY || ''],
              Posting_Ptr: [this.details.ptrDetail?.POSTING_PTR || ''],
              Stage_key: [this.rowData.TRANS_NO || '']
            });
            this.formRows.push(row);
          });
          this.loader = false;
        });
      }
    });
  }

  findDetails(dfDetails: any, ptrDropdownMap: { [key: number]: any[] }, mapKey: number) {
    let ptrDetail;
    const ptrDropdown = ptrDropdownMap[mapKey];

    if (ptrDropdown) {
      let ptrItem = ptrDropdown.find(item => item.PTR_CC_KEY === dfDetails.PTR_CC_KEY);
      if (ptrItem) {
        ptrDetail = {
          PTR_DTLS: ptrItem.PTR_DTLS,
          POSTING_PTR: ptrItem.POSTING_PTR
        };
      }
    }

    return { ptrDetail };
  }

  onSelectChange(event: any, rowIndex: number) {
    const selectedValue = parseInt(event.target.value, 10);
    let items = this.ptrDropdownMap[rowIndex];
    if (items) {
      let selectedItem = items.find(item => item.PTR_CC_KEY === selectedValue);
      if (selectedItem) {
        const group = <UntypedFormGroup>this.formRows.controls[rowIndex];
        group.get('Posting_Ptr')?.setValue(selectedItem.POSTING_PTR);
      }
    }
  }
  onPshipDropdownChange(event: any, rowIndex: number): void {
    this.isLoadingMap[rowIndex] = true;
    const selectedPshipCCKey = event.target.value;

    const group = <UntypedFormGroup>this.formRows.controls[rowIndex];
    group.get('Posting_Ptr')?.setValue('');
    group.get('Ptr_CC_key')?.setValue('');
    let params = {
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key,
      stage_key: this.rowData?.TRANS_NO,
    }
    this.dfAssignEntityService.fetchPtrDropdown(selectedPshipCCKey, this.baseURLs.api, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ptrDropdownResult => {
      this.ptrDropdownMap[rowIndex] = ptrDropdownResult.jsonObject;
      this.isLoadingMap[rowIndex] = false;
    }, error => {
      console.error('Error fetching dropdown data:', error);
      this.isLoadingMap[rowIndex] = false;
    });
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {

    if (this.form.status === 'INVALID' || this.loader == true) {
      this.gtwSnackbarService.openSnackBar("Please check form details and try again", 'gtw-snackbar--red');
      return;
    }
    this.loader = true;
    const formValues = this.formRows.controls.map((control: { value: any; }) => control.value);
    let params = {
      tax_year: this.tax_year,
      scenario: this.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key
    }
    let clob_settings = { tax_year: params.tax_year, scenario: params.scenario, jcd_key: params.jcd_key };
    let clob_data = formValues;
    this.dfAssignEntityService.saveEntityAssign(this.baseURLs.api, params, clob_settings, clob_data, this.scenarioCode, this.isIssueKeyEnabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data !== 'error') {
        this.requestGridUpdate.emit('refresh');
        this.loader = false;
      }
      else {
        this.gtwSnackbarService.openSnackBar("Please check form details and try again", 'gtw-snackbar--red');
        this.loader = false;
      }
    }, () => {
      this.gtwSnackbarService.openSnackBar("Error while saving", 'gtw-snackbar--red');
      this.loader = false;
    });
  }

}

